import type {
  ChangePasswordInput,
  ConfirmPasswordResetInput,
  CurrentUser,
  LoginInput,
  RegisterInput,
  SessionResponse,
  SessionSummary,
} from '@talkvibe/shared';
import { api, setCsrfToken } from './api.ts';

/**
 * The device id is the one piece of identity the client is allowed to keep.
 *
 * It is not a credential: presenting it proves nothing, and the server ignores
 * one that does not belong to the authenticating user. It exists so that
 * signing in again from this browser updates the existing device entry instead
 * of adding a new row to the user's session list every time.
 */
const DEVICE_KEY = 'tv.deviceId';

function readDeviceId(): string | undefined {
  try {
    return window.localStorage.getItem(DEVICE_KEY) ?? undefined;
  } catch {
    // Private mode, or storage blocked. A new device row is the worst outcome.
    return undefined;
  }
}

function rememberDevice(session: SessionResponse): void {
  if (!session.device) return;
  try {
    window.localStorage.setItem(DEVICE_KEY, session.device.id);
  } catch {
    // Not fatal - see above.
  }
}

function forgetDevice(): void {
  try {
    window.localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/** Best-effort platform hint, so the session list reads sensibly. */
function platformHint(): LoginInput['platform'] {
  return 'web';
}

/**
 * What registering answers now.
 *
 * `pending` is the only shape the current API returns: the signup is held and
 * an account appears when the link in the email is opened. `SessionResponse` is
 * kept in the union on purpose — the API and this app deploy separately, and
 * for the minutes between the two a browser running the new code may be talking
 * to the old server, which still signs people straight in. Handling both is
 * what makes the order of those two deploys not matter.
 */
export type RegisterResult =
  | { pending: true; email: string }
  | { pending: false; session: SessionResponse };

export const authService = {
  async register(input: RegisterInput): Promise<RegisterResult> {
    const answer = await api.post<SessionResponse & { pending?: boolean; email?: string }>(
      '/api/auth/register',
      {
        ...input,
        deviceId: readDeviceId(),
        platform: platformHint(),
      },
    );

    if (answer.pending === true) {
      return { pending: true, email: answer.email ?? input.email };
    }

    setCsrfToken(answer.csrfToken);
    rememberDevice(answer);
    return { pending: false, session: answer };
  },

  async login(input: LoginInput): Promise<SessionResponse> {
    const session = await api.post<SessionResponse>('/api/auth/login', {
      ...input,
      deviceId: readDeviceId(),
      platform: platformHint(),
    });
    setCsrfToken(session.csrfToken);
    rememberDevice(session);
    return session;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } finally {
      // Clear locally even if the call failed - the user asked to sign out.
      // The device id is deliberately kept: it identifies the browser, not the
      // session, and keeping it avoids a duplicate entry on the next sign-in.
      setCsrfToken(null);
    }
  },

  /**
   * Restores the session on page load. Returns null when signed out, which is
   * an ordinary outcome here rather than an error.
   */
  async restoreSession(): Promise<SessionResponse | null> {
    try {
      const session = await api.get<SessionResponse>('/api/auth/session', {
        ignoreUnauthorized: true,
      });
      setCsrfToken(session.csrfToken);
      rememberDevice(session);
      return session;
    } catch {
      setCsrfToken(null);
      return null;
    }
  },

  /** Rotates the session token. The cookie is replaced by the response. */
  async refresh(): Promise<SessionResponse> {
    const session = await api.post<SessionResponse>('/api/auth/refresh');
    setCsrfToken(session.csrfToken);
    rememberDevice(session);
    return session;
  },

  me: (): Promise<CurrentUser> => api.get<CurrentUser>('/api/me'),

  /* --- Session management --- */

  sessions: (): Promise<SessionSummary[]> => api.get<SessionSummary[]>('/api/auth/sessions'),

  revokeSession: (id: string): Promise<void> =>
    api.delete(`/api/auth/sessions/${encodeURIComponent(id)}`).then(() => undefined),

  logoutEverywhereElse: (): Promise<void> =>
    api.post('/api/auth/logout-all', { includeCurrent: false }).then(() => undefined),

  /* --- Email verification --- */

  requestEmailVerification: (): Promise<void> =>
    api.post('/api/auth/verify-email/request').then(() => undefined),

  /**
   * Opens a verification link.
   *
   * `created` says which of the two things happened: an account came into
   * existence and its owner should now sign in, or somebody who already had one
   * confirmed their address and is already where they wanted to be.
   */
  confirmEmailVerificationResult: (token: string): Promise<{ created: boolean }> =>
    api.post<{ ok: boolean; created?: boolean }>('/api/auth/verify-email/confirm', { token })
      .then((answer) => ({ created: answer.created === true })),

  confirmEmailVerification: (token: string): Promise<void> =>
    api.post('/api/auth/verify-email/confirm', { token }).then(() => undefined),

  /* --- Password --- */

  /**
   * The wire field is `email`, but the server matches it against the username
   * column too (`WHERE username = ? OR email = ?`), so either is accepted.
   * The parameter is named for what it holds rather than for the key it is
   * sent under.
   */
  requestPasswordReset: (identifier: string): Promise<void> =>
    api.post('/api/auth/password-reset/request', { email: identifier }).then(() => undefined),

  confirmPasswordReset: (input: ConfirmPasswordResetInput): Promise<void> =>
    api.post('/api/auth/password-reset/confirm', input).then(() => {
      forgetDevice();
    }),

  changePassword: (input: ChangePasswordInput): Promise<void> =>
    api.post('/api/auth/password', input).then(() => undefined),
};
